import React from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from './components/ui/sidebar';
import { Toaster } from "sonner"
import SideBar from './components/SideBar';
import { BillingProvider } from './components/billing/BillingProvider';
import PendingPurchaseCleanup from './components/billing/PendingPurchaseCleanup';
import { Gift, Menu, Sparkles } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from './lib/redux/store';

const getMonthDay = (dateValue?: string | null) => {
    if (!dateValue) return null;

    const dateOnlyMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
        return {
            month: Number(dateOnlyMatch[2]),
            day: Number(dateOnlyMatch[3]),
        };
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    return {
        month: date.getMonth() + 1,
        day: date.getDate(),
    };
};

const isBirthdayToday = (dob?: string | null) => {
    const birthday = getMonthDay(dob);
    if (!birthday) return false;

    const today = new Date();
    return birthday.month === today.getMonth() + 1 && birthday.day === today.getDate();
};

const BirthdayBanner = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    if (!agent || !isBirthdayToday(agent.dob)) return null;

    const firstName = String(agent.firstName || '').trim();
    const name = firstName || 'Agent';

    return (
        <div className="mb-4 overflow-hidden rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-amber-600 shadow-sm">
                    <Gift className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold">Happy Birthday, {name}!</p>
                        <Sparkles className="h-4 w-4 text-amber-500" />
                    </div>
                    <p className="mt-0.5 text-sm text-amber-800">
                        Wishing you a wonderful day and a strong year ahead.
                    </p>
                </div>
            </div>
        </div>
    );
};

const Layout = () => {
    return (
        // The sidebar remembers being collapsed in a cookie; start the way it was left.
        <SidebarProvider defaultOpen={!document.cookie.includes('sidebar_state=false')}>
            <BillingProvider>
            <div className="flex min-h-screen w-full">
                <SideBar />
                <main className="min-w-0 flex-1 overflow-x-hidden bg-background">
                    <Toaster position="top-center" richColors/>
                    <PendingPurchaseCleanup />
                    <div className='min-h-screen w-full'>
                        <div className="sticky top-0 z-20 border-b bg-white/95 px-4 py-3 backdrop-blur md:hidden">
                            <SidebarTrigger className="inline-flex cursor-pointer items-center justify-center rounded-md">
                                <Menu className="h-6 w-6" color='black'/>
                            </SidebarTrigger>
                        </div>
                        <div className='mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8'>
                            <BirthdayBanner />
                            <Outlet />
                        </div>
                    </div>
                </main>
            </div>
            </BillingProvider>
        </SidebarProvider>
    );
};

export default Layout;
